import { useListState } from "@/hooks/use-list-state";
import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchQueryParam } from "@/hooks/use-search-query-param";
import { matchesSearch } from "@/lib/global-search";
import { useLocation, Link } from "wouter";
import { rowNavHandlers } from "@/lib/row-nav";
import { usePermissions, ViewOnlyBanner } from "@/hooks/use-permissions";
import type { Property, ThirdPartyListing, MarketingSettings, CustomListingSite } from "@shared/schema";
import { activeListingCounts, totalActiveListings, compareProperties, groupProperties, readListPreferences, SORT_LABELS, displayRoomCount, type PropertyListRow, type SortCol, type SortDir, type GroupBy } from "@/lib/properties-list";

import AppLayout from "@/components/app-layout";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { FounderAllotmentWarningBanner } from "@/components/founder-allotment-warning-banner";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Home, BookOpen, Layers, ArrowUp, ArrowDown, ArrowUpDown, X, Search } from "lucide-react";
import PropertyMap from "@/components/property-map";
import { AddPropertyDialog } from "@/components/add-property-dialog";
import { BulkImportExportDialog } from "@/components/bulk-import-export-dialog";
import { ManageGroupsDialog } from "@/components/property-group-select";
import { PropertyGroupFilter } from "@/components/property-group-filter";
import { JourneyEmptyState, useJourneyStage } from "@/lib/journey-empty-states";
import { usePageTitle } from "@/hooks/use-page-title";

const DEFAULT_LISTING_PLATFORMS: { id: string; name: string; color: string; textColor: string }[] = [
  { id: "zillow", name: "Zillow", color: "bg-blue-600", textColor: "text-white" },
  { id: "roomies", name: "Roomies", color: "bg-orange-500", textColor: "text-white" },
  { id: "spare_room", name: "Spare Room", color: "bg-green-600", textColor: "text-white" },
  { id: "hotpads", name: "HotPads", color: "bg-red-500", textColor: "text-white" },
  { id: "facebook_marketplace", name: "Facebook Marketplace", color: "bg-blue-700", textColor: "text-white" },
  { id: "craigslist", name: "Craigslist", color: "bg-purple-600", textColor: "text-white" },
  { id: "furnish_finders", name: "Furnish Finders", color: "bg-teal-600", textColor: "text-white" },
];

function platformInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .substring(0, 2)
    .toUpperCase();
}

type PlatformInfo = { id: string; name: string; color: string; textColor: string };

function PlatformBreakdownCell({
  property,
  counts,
  customSites,
}: {
  property: Property;
  counts: Map<string, number>;
  customSites: CustomListingSite[];
}) {
  const platforms: PlatformInfo[] = useMemo(() => {
    const customs: PlatformInfo[] = customSites.map((s) => ({
      id: s.id,
      name: s.name,
      color: s.color || "bg-gray-500",
      textColor: "text-white",
    }));
    return [...DEFAULT_LISTING_PLATFORMS, ...customs];
  }, [customSites]);

  const totalActive = totalActiveListings(counts);

  // Include any unknown platform ids that have listings, after the known ones.
  const knownIds = new Set(platforms.map((p) => p.id));
  const extraPlatforms: PlatformInfo[] = Array.from(counts.keys())
    .filter((id) => !knownIds.has(id))
    .map((id) => ({ id, name: id, color: "bg-gray-400", textColor: "text-white" }));
  const allPlatforms = [...platforms, ...extraPlatforms];

  const missingCount = allPlatforms.filter((p) => (counts.get(p.id) ?? 0) === 0).length;

  const [open, setOpen] = useState(false);
  const keyboardOpenRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(() => () => clearTimers(), []);

  const scheduleOpen = () => {
    clearTimers();
    openTimerRef.current = setTimeout(() => setOpen(true), 150);
  };

  const scheduleClose = () => {
    clearTimers();
    closeTimerRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            clearTimers();
            setOpen((prev) => !prev);
          }}
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
              e.stopPropagation();
              keyboardOpenRef.current = true;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                clearTimers();
                if (open) {
                  contentRef.current?.querySelector<HTMLAnchorElement>("a[href]")?.focus();
                  keyboardOpenRef.current = false;
                } else {
                  setOpen(true);
                }
              }
            }
          }}
          aria-label={`${totalActive} active listings for ${property.name}; show platform details`}
          className={`inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-md text-sm font-semibold border cursor-pointer transition-colors ${
            totalActive > 0
              ? "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              : "bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100"
          }`}
          data-testid={`button-platform-breakdown-${property.id}`}
          title={`${totalActive} active listing${totalActive === 1 ? "" : "s"} — hover or click for breakdown`}
        >
          {totalActive}
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        align="start"
        className="w-72 p-2"
        data-testid={`popover-platform-breakdown-${property.id}`}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (keyboardOpenRef.current) contentRef.current?.querySelector<HTMLAnchorElement>("a[href]")?.focus();
          keyboardOpenRef.current = false;
        }}
      >
        <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Active listings by platform
        </div>
        <div className="flex flex-col">
          {allPlatforms.map((p) => {
            const count = counts.get(p.id) ?? 0;
            return (
              <Link
                key={p.id}
                href={`/app/marketing/third-party-listings?propertyId=${property.id}&platform=${encodeURIComponent(p.id)}`}
              >
                <span
                  className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-md text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  data-testid={`link-property-${property.id}-platform-${p.id}`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <span className={`inline-flex items-center justify-center h-5 w-5 rounded text-[10px] font-bold ${count > 0 ? `${p.color} ${p.textColor}` : "bg-gray-100 text-gray-400 border border-dashed border-gray-300"}`}>
                      {platformInitials(p.name)}
                    </span>
                    <span className="truncate">{p.name}</span>
                  </span>
                  <span
                    className={`inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full text-xs font-semibold ${count > 0 ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-400"}`}
                    data-testid={`text-property-${property.id}-platform-count-${p.id}`}
                  >
                    {count}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
        <div className="border-t mt-1 pt-1 px-2 py-1 text-xs text-gray-500" data-testid={`text-property-${property.id}-platform-summary`}>
          {totalActive} active listing{totalActive === 1 ? "" : "s"}
          {missingCount > 0 && ` · ${missingCount} platform${missingCount === 1 ? "" : "s"} with no coverage`}
        </div>
        <div className="px-2 pt-1">
          <Link href={`/app/marketing/third-party-listings?propertyId=${property.id}`}>
            <span
              className="block px-0 py-1 text-xs text-blue-600 hover:underline cursor-pointer"
              data-testid={`link-property-${property.id}-platform-view-all`}
            >
              View all listings for this property →
            </span>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function statusBadge(status: string) {
  switch (status) {
    case "active":
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>;
    case "underwriting":
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Underwriting</Badge>;
    case "renovations":
      return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Renovations</Badge>;
    case "archive":
      return <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100">Archive</Badge>;
    case "launch":
      return <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Launch</Badge>;
    case "acquisition":
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Acquiring</Badge>;
    case "sold":
      return <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100">Sold</Badge>;
    case "disposed":
      return <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100">Disposed</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

export default function AppProperties() {
  return (
    <AppLayout><PropertiesList /></AppLayout>
  );
}

function PropertiesList() {
  usePageTitle("Properties");
  const [, navigate] = useLocation();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("properties");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [searchQuery, setSearchQuery, incomingSearch] = useSearchQueryParam();
  // /app/properties?add=1 (the connect-PadSplit screen's "Add a property" link) opens the add dialog.
  const [openAddFromLink] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("add") === "1",
  );
  const [showArchived, setShowArchived] = useListState("properties:archived", incomingSearch !== null, incomingSearch ?? "");
  const [groupFilter, setGroupFilter] = useListState<string>("properties:group", "all", incomingSearch ?? "");
  const previousIncomingSearch = useRef(incomingSearch);
  useEffect(() => {
    if (previousIncomingSearch.current === incomingSearch) return;
    previousIncomingSearch.current = incomingSearch;
    if (incomingSearch === null) return;
    setShowArchived(true);
    setGroupFilter("all");
  }, [incomingSearch]);
  const [preferences] = useState(() => readListPreferences(typeof window === "undefined" ? undefined : window.localStorage));
  const [groupBy, setGroupBy] = useState<GroupBy>(preferences.groupBy);
  const [sortCol, setSortCol] = useState<SortCol>(preferences.sortCol);
  const [sortDir, setSortDir] = useState<SortDir>(preferences.sortDir);
  useEffect(() => {
    window.localStorage.removeItem("properties:stateFilter");
    window.localStorage.removeItem("properties:groupByState");
  }, []);
  useEffect(() => {
    window.localStorage.setItem("properties:groupBy", groupBy);
  }, [groupBy]);
  useEffect(() => {
    window.localStorage.setItem("properties:sortCol", sortCol);
    window.localStorage.setItem("properties:sortDir", sortDir);
  }, [sortCol, sortDir]);

  const resetSort = () => { setSortCol("name"); setSortDir("asc"); };
  const cycleSort = (col: SortCol) => {
    if (sortCol !== col) {
      setSortCol(col);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      resetSort();
    }
  };
  const journeyStage = useJourneyStage();

  const propertiesQuery = useQuery<PropertyListRow[]>({
    queryKey: ["/api/app/properties"],
  });
  const thirdPartyListingsQuery = useQuery<ThirdPartyListing[]>({
    queryKey: ["/api/app/marketing/third-party-listings"],
  });
  const marketingSettingsQuery = useQuery<MarketingSettings | null>({
    queryKey: ["/api/app/marketing/settings"],
  });
  const properties: PropertyListRow[] = propertiesQuery.data || [];
  const thirdPartyListings = thirdPartyListingsQuery.data || [];
  const listingCounts = useMemo(() => activeListingCounts(thirdPartyListings), [thirdPartyListings]);
  const customListingSites: CustomListingSite[] = Array.isArray(marketingSettingsQuery.data?.customListingSites)
    ? (marketingSettingsQuery.data!.customListingSites as CustomListingSite[])
    : [];
  const filteredFlat = properties.filter((p) => {
    if (!showArchived && (p.status === "archive" || p.status === "sold" || p.status === "disposed")) return false;
    if (groupFilter !== "all" && (p.propertyGroup || "") !== groupFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return matchesSearch(q, [
        p.name, p.propertyGroup, p.address, p.city, p.state, p.postalCode,
        "property properties house houses home homes",
      ]);
    }
    return true;
  });

  const filtered = [...filteredFlat].sort((a, b) => compareProperties(a, b, sortCol, sortDir, listingCounts));
  const grouped = groupProperties(filtered, groupBy);

  const renderRow = (prop: PropertyListRow) => (
    <TableRow
      key={prop.id}
      className="cursor-pointer hover:bg-gray-50"
      {...rowNavHandlers(`/app/properties/${prop.id}`, navigate)}
      data-testid={`row-property-${prop.id}`}
    >
      <TableCell className="font-medium text-gray-900" data-testid={`text-property-name-${prop.id}`}>
        <div className="-m-4 p-4">
        <div className="flex items-center gap-2">
          <Link href={`/app/properties/${prop.id}`} onClick={(e) => e.stopPropagation()} className={prop.parentPropertyId ? "ml-6" : ""} title={prop.name || undefined}>
            {prop.parentPropertyId ? `└ ${prop.name}` : prop.name}
          </Link>
          {prop.sopLink && (
            <a
              href={prop.sopLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs font-normal text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              data-testid={`link-property-sop-${prop.id}`}
              title="Open SOP in a new tab"
            >
              <BookOpen className="h-3 w-3" />
              SOP
            </a>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5" data-testid={`text-property-location-${prop.id}`}>
          {[prop.city, prop.state, prop.postalCode].filter(Boolean).join(", ") || "—"}
          {prop.parentPropertyId && <span className="ml-2">Unit of {properties.find((p) => p.id === prop.parentPropertyId)?.name ?? `property #${prop.parentPropertyId}`}</span>}
        </div>
        </div>
      </TableCell>
      <TableCell data-testid={`text-property-status-${prop.id}`}>
        {statusBadge(prop.status)}
      </TableCell>
      <TableCell data-testid={`text-property-state-${prop.id}`}>{prop.state?.trim() || "—"}</TableCell>
      <TableCell data-testid={`text-property-rooms-${prop.id}`}>{displayRoomCount(prop)}</TableCell>
      <TableCell onClick={(e) => e.stopPropagation()} data-testid={`cell-property-listings-${prop.id}`}>
        <PlatformBreakdownCell
          property={prop}
          counts={listingCounts.get(prop.id) ?? new Map()}
          customSites={customListingSites}
        />
      </TableCell>
      <TableCell data-testid={`text-property-group-${prop.id}`}>{prop.propertyGroup || "—"}</TableCell>
      <TableCell className="text-gray-500 text-sm font-mono" data-testid={`text-property-padsplit-id-${prop.id}`}>
        {prop.padsplitPropertyId || "—"}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()} data-testid={`cell-property-setup-progress-${prop.id}`}>
        <Link
          href={`/app/properties/${prop.id}?checklist=open`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex min-h-9 min-w-12 items-center justify-center rounded-md px-2 text-sm font-semibold text-brand hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          aria-label={prop.setupProgress
            ? `Open ${prop.name} setup checklist, ${prop.setupProgress.percent}% complete`
            : `Open ${prop.name} setup checklist; progress unavailable`}
          data-testid={`link-property-setup-progress-${prop.id}`}
        >
          {prop.setupProgress ? `${prop.setupProgress.percent}%` : "—"}
        </Link>
      </TableCell>
    </TableRow>
  );

  return (
      <div className="p-4 sm:p-6 min-w-0">
        <ViewOnlyBanner area="properties" />
        <FounderAllotmentWarningBanner />
        <div className="space-y-3 mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-properties-heading" data-tour="properties-heading">Properties</h1>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start justify-between gap-3" data-testid="properties-toolbar">
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto min-w-0 flex-1" data-testid="properties-list-controls" data-tour="properties-filters">
            <div className="relative w-full sm:w-[260px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search name, address, city, state, zip..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-full pl-9 text-sm"
                aria-label="Search properties"
                data-testid="input-property-search"
              />
            </div>
            <PropertyGroupFilter
              properties={properties}
              value={groupFilter}
              onChange={setGroupFilter}
              triggerProps={{
                title: groupFilter === "all" ? undefined : groupFilter,
              }}
            />
            {(searchQuery || groupFilter !== "all" || showArchived) && (
              <Button variant="outline" size="sm" onClick={() => {
                setSearchQuery("");
                setGroupFilter("all");
                setShowArchived(false);
              }} data-testid="button-clear-property-filters">
                <X className="h-4 w-4 mr-1" /> Clear filters
              </Button>
            )}
              <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupBy)}>
                <SelectTrigger className="h-9 w-[210px] text-sm" aria-label="Group by" data-testid="select-group-by">
                  <span className="text-muted-foreground mr-1">Group by:</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="state">State</SelectItem>
                  <SelectItem value="propertyGroup">Property group</SelectItem>
                </SelectContent>
              </Select>
            {(sortCol !== "name" || sortDir !== "asc") && (
              <Button
                variant="outline"
                size="sm"
                onClick={resetSort}
                data-testid="button-clear-sort"
                title="Clear current sort"
              >
                <X className="h-4 w-4 mr-1" /> Clear sort: {SORT_LABELS[sortCol]} {sortDir === "asc" ? "↑" : "↓"}
              </Button>
            )}
            <div className="flex items-center gap-2 h-9 shrink-0">
              <Checkbox id="properties-show-archived" checked={showArchived} onCheckedChange={(checked) => setShowArchived(checked === true)} data-testid="checkbox-show-archived" />
              <label htmlFor="properties-show-archived" className="text-sm cursor-pointer whitespace-nowrap">Show archived</label>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-auto" aria-label="Properties help" data-testid="properties-help">
            <ExplainerVideoButton pageKey="properties" />
            <GuideButton pageKey="properties" />
          </div>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="properties-actions">
            <AddPropertyDialog
              disabled={readOnly}
              triggerLabel="Add property"
              triggerTestId="button-add-property"
              data-tour="properties-add"
              defaultOpen={openAddFromLink}
            />
            <ManageGroupsDialog triggerLabel="Add property group" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkOpen(true)}
              data-tour="properties-bulk-import-export"
              data-testid="button-bulk-import-export"
            >
              <Layers className="h-4 w-4 mr-1" /> Bulk Import / Export
            </Button>
            <BulkImportExportDialog
              open={bulkOpen}
              onOpenChange={setBulkOpen}
              title="Bulk Import / Export"
              description="Add many records at once from a spreadsheet, or export your current list."
              tabs={[
                {
                  value: "properties",
                  label: "Properties",
                  templateUrl: "/api/app/properties/bulk/template",
                  exportUrl: "/api/app/properties/bulk/export",
                  importUrl: "/api/app/properties/bulk",
                  invalidateQueryKey: ["/api/app/properties"],
                  entityNounPlural: "properties",
                },
                {
                  value: "appliances",
                  label: "Appliances",
                  templateUrl: "/api/app/appliances/bulk/template",
                  exportUrl: "/api/app/appliances/bulk/export",
                  importUrl: "/api/app/appliances/bulk",
                  invalidateQueryKey: ["/api/app/appliances"],
                  entityNounPlural: "appliances",
                },
              ]}
            />
          </div>
          <GuideNudgeBanner pageKey="properties" />
        </div>

        <PropertyMap properties={properties} showLegend={true} showTrashCoverage={true} />

        {propertiesQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        ) : filtered.length === 0 ? (
          properties.length === 0 ? (
            <JourneyEmptyState
              page="properties"
              stage={journeyStage}
              icon={<Home className="h-10 w-10 text-gray-300" />}
            />
          ) : (
            <div className="text-center py-16" data-testid="empty-properties-filtered">
              <p className="text-gray-500 mb-2">No properties match your filters</p>
              <p className="text-sm text-gray-400">Try clearing search or group filters.</p>
            </div>
          )
        ) : (
          <div className="border rounded-lg overflow-hidden" data-tour="properties-table">
            <Table className="min-w-[980px]">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  {([
                    { col: "name", id: "property-details", buttonId: "name", label: "Property Details" },
                    { col: "status", id: "status", buttonId: "status", label: "Status" },
                    { col: "state", id: "state", buttonId: "state", label: "State" },
                    { col: "rooms", id: "rooms", buttonId: "rooms", label: "Rooms" },
                    { col: "listings", id: "listings", buttonId: "listings", label: "Listings" },
                    { col: "propertyGroup", id: "group", buttonId: "group", label: "Property Group*" },
                    { col: "padsplitId", id: "padsplit-id", buttonId: "padsplit-id", label: "PadSplit ID" },
                    { col: "setupProgress", id: "setup-progress", buttonId: "setup-progress", label: "Setup" },
                  ] satisfies { col: SortCol; id: string; buttonId: string; label: string }[]).map(({ col, id, buttonId, label }) => (
                    <TableHead key={col} data-testid={`th-${id}`} aria-sort={sortCol === col ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
                      <button
                        type="button"
                        onClick={() => cycleSort(col)}
                        className="inline-flex items-center gap-1 whitespace-nowrap hover:text-gray-900"
                        data-testid={`button-sort-${buttonId}`}
                        title={sortCol === col ? `Sorted ${sortDir === "asc" ? "ascending — click for descending" : "descending — click to reset to name A–Z"}` : `Click to sort by ${SORT_LABELS[col]}`}
                        aria-describedby={col === "listings" ? "properties-listings-hint" : undefined}
                      >
                        <span>{label}</span>
                        {sortCol === col ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" aria-hidden="true" /> : <ArrowDown className="h-3 w-3" aria-hidden="true" />) : <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />}
                      </button>
                      {col === "listings" && <div id="properties-listings-hint" className="text-xs font-normal whitespace-nowrap">Hover for details</div>}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupBy !== "none"
                  ? grouped.flatMap((g) => [
                      <TableRow key={`group-header-${g.key}`} className="bg-gray-100 hover:bg-gray-100">
                        <TableCell
                          colSpan={8}
                          className="text-xs font-semibold uppercase tracking-wide text-gray-600"
                          data-testid={`row-${groupBy}-header-${g.key || "unset"}`}
                        >
                          {g.label} · {g.items.length} {g.items.length === 1 ? "property" : "properties"}
                        </TableCell>
                      </TableRow>,
                      ...g.items.map(renderRow),
                    ])
                  : filtered.map(renderRow)}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
  );
}
